import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { contactInfo } from '../data';

const CITIES_DATA = {
  koebenhavn: {
    cityName: 'København',
    regionName: 'Storkøbenhavn og Nordsjælland',
    slug: 'laserrensning-koebenhavn',
    h1: 'Laserrensning i København til ejendomme, byggeri og restaurering',
    metaTitle: 'Laserrensning i København til ejendomme og byggeri | Få tilbud',
    metaDesc:
      'Mobil og professionel laserrensning i København til facader, murværk, natursten og industri. 100% vand- og kemifri afrensning. Kontakt os for tilbud.',
    heroImage: '/cdn-cgi/image/width=1800,quality=80,format=auto/_assets/img-3504.jpg',
    sidebarImage: '/cdn-cgi/image/width=800,quality=85,format=auto/_assets/front-kbh-chr-d-9-high.webp',
    topBanner: {
      image: '/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/sandstensfigurer-kbh-h.jpg',
      alt: 'Sandstensfigurer på Københavns Hovedbanegård renset med laser',
      caption: 'Københavns Hovedbanegård: 100 års sodskorpe fjernet tørt med punktudsugning'
    },
    intro: [
      <>
        Ecolaser udfører professionel og mobil{' '}
        <Link
          to="/"
          className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
        >
          laserrensning i København
        </Link>{' '}
        for bygherrer, entreprenører, rådgivere, ejendomsselskaber, styrelser, kommuner og andre offentlige aktører. Vi arbejder med præcis afrensning af facader, murværk, natursten, beton, metal og tekniske overflader på både eksisterende ejendomme og byggeprojekter.
      </>,
      'Vores løsninger inden for laserrens arbejder uden procesvand, rensekemi og blæsemidler. Det gør metoden relevant ved blandt andet renovering, restaurering, nybyggeri og vedligeholdelse, hvor materialet og det færdige udtryk stiller særlige krav til afrensningen.'
    ],
    sections: [
      {
        id: 'kontrolleret-afrensning',
        title: 'Kontrolleret laserafrensning til projekter i København',
        paragraphs: [
          'Ved laserafrensning i København tilpasses laserens intensitet og frekvens til materialet og den belægning, der skal fjernes. Sod, trafikfilm, kalkslør, graffiti, rust og andre fastsiddende belægninger kræver forskellige indstillinger og kan ikke behandles efter én fast standard.',
          'Før arbejdet vurderer vi derfor overfladens alder, hårdhed, porøsitet, erosionsgrad og tilstand. På større eller mere følsomme projekter kan en prøverensning bruges til at fastlægge renseniveau og arbejdsmetode.',
          'Det gør det muligt at indgå i projekter fra enkelte bygningsdele til større facade- og renoveringsopgaver, hvor bygherre, rådgiver og entreprenør har konkrete krav til den færdige overflade.'
        ]
      },
      {
        id: 'opgaver-i-byen',
        title: 'Opgaver med laserrens i København',
        introText: 'Vi løser en bred vifte af opgaver for både private ejendomsejere, entreprenører og offentlige institutioner:',
        bullets: [
          'Kalkslør og salpeterudtræk på nyt og eksisterende murværk',
          'Sod, trafikfilm og atmosfærisk smuds på facader og natursten',
          <>
            <Link
              to="/ydelser/graffiti-beton-natursten"
              className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
            >
              Graffiti og malingsrester
            </Link>{' '}
            på egnede mineralske overflader
          </>,
          'Historiske bygningsdele, stuk, friser og ornamenter',
          <>
            <Link
              to="/ydelser/rust-metaloverflader"
              className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
            >
              Rust og belægninger på metaldele
            </Link>{' '}
            og tekniske installationer
          </>,
          'Afrensning i forbindelse med nybyggeri, renovering og vedligeholdelse'
        ]
      },
      {
        id: 'facaderens-murvaerk',
        title: 'Facaderens med laser på murværk og natursten',
        paragraphs: [
          'Københavnske ejendomme spænder fra nyere byggeri til ældre murstensfacader og natursten med meget forskellige egenskaber. Ved laserrens af mursten vurderer vi blandt andet brænding, brandhud, fuger og erosionsgrad, før behandlingen fastlægges.',
          'Da processen ikke tilfører procesvand til murstenenes kapillarer og ikke arbejder med mekanisk slibning, kan den være relevant på overflader, hvor en mere kontrolleret behandling er nødvendig.',
          <>
            På projekter med særlige krav til facade og murværk anvender vi samme materialefaglige tilgang som ved vores{' '}
            <Link
              to="/ydelser/facaderens-murvaerk"
              className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
            >
              facaderens med laser til murværk
            </Link>
            .
          </>
        ],
        showcase: {
          image: '/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/img-3435.jpg',
          alt: 'Laserrensning af murværk og facade i Storkøbenhavn',
          caption: '100% tør laserafrensning af murstensfacader uden syre eller procesvand.'
        }
      },
      {
        id: 'historiske-ejendomme',
        title: 'Historiske ejendomme kræver respekt for detaljen',
        paragraphs: [
          'På fredede, bevaringsværdige og historiske bygninger handler afrensning ikke om at få materialet til at fremstå nyt. Historisk patina, profileringer, fuger og stenhuggerdetaljer kan være en vigtig del af bygningens udtryk og skal indgå i vurderingen.',
          'Laserens parametre kan efter en materialetest tilpasses blandt andet sandsten, kalksten, marmor, granit, historiske gipsarbejder, friser og ornamenter. Ecolaser har blandt andet arbejdet med afrensning af historiske sandstensfigurer på Københavns Hovedbanegård.',
          <>
            Denne type opgaver bygger på samme principper som vores arbejde med{' '}
            <Link
              to="/ydelser/fredede-bygninger"
              className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
            >
              laserrensning af fredede og bevaringsværdige bygninger
            </Link>
            , hvor materialeforståelse og det ønskede renseniveau er afgørende.
          </>
        ],
        showcase: {
          image: '/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/ornament-650x650.jpg',
          alt: 'Afrensning af historisk ornament på søjle',
          caption: 'Skånsom laserrensning af historiske profileringer og ornamenter uden slid på overfladestrukturen.'
        }
      },
      {
        id: 'tekniske-overflader',
        title: 'Laserrens til tekniske overflader og skadeservice',
        paragraphs: [
          'Laserrens kan også anvendes på metal, procesudstyr og tekniske overflader i forbindelse med drift, vedligeholdelse og skadeservice. Rust, olie, fedt, maling og andre belægninger kan behandles målrettet, når underlaget egner sig til metoden.',
          <>
            Det mobile udstyr gør det muligt at arbejde direkte på ejendommen, byggepladsen eller det tekniske anlæg. Ved denne type opgaver arbejder vi efter de samme principper som inden for{' '}
            <Link
              to="/ydelser/industri-skadeservice"
              className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
            >
              laserrens til industri og skadeservice
            </Link>
            .
          </>,
          'Når opgaven kræver det, kan punktudsugning med HEPA-filtrering anvendes til støv og forstøvede restpartikler.'
        ]
      },
      {
        id: 'mobil-daekning',
        title: 'Mobil laserrens i København og hovedstadsområdet',
        paragraphs: [
          'Ecolaser udfører mobile opgaver i København og resten af hovedstadsområdet for blandt andet bygherrer, entreprenører, ejendomsselskaber, rådgivere, kommuner, styrelser og virksomheder.',
          'Materiale, belægning, areal, geometri og adgangsforhold indgår i vurderingen af projektet. Ved komplekse eller større opgaver kan en besigtigelse eller prøverensning være relevant, før metode og tilbud fastlægges.',
          'Ring på 29 10 08 10, skriv til info@ecolaser.dk eller kontakt os for en faglig vurdering af projektet.'
        ]
      }
    ],
    cases: [
      {
        title: 'Københavns Hovedbanegård',
        category: 'DSB Kulturarv',
        desc: 'Over 100 års sodskorpe fjernet fra historiske sandstensfigurer uden slid på stenhuggerdetaljer.',
        image: '/cdn-cgi/image/width=600,quality=80,format=auto/_assets/sandstensfigurer-kbh-h.jpg',
        link: '/cases/kobenhavns-hovedbanegard-sandstensfigurer'
      },
      {
        title: 'Funkishus i Hellerup',
        category: 'Gule Mursten & Facade',
        desc: 'Fjernelse af saltudslag, sod og snavs i op til 9 meters højde med mobilt udstyr.',
        image: '/cdn-cgi/image/width=600,quality=80,format=auto/_assets/hellerup-for-og-efter-650x650.jpg',
        link: '/cases/skaansom-laserrens-af-bevaringsvaerdigt-funkishus-i-hellerup'
      },
      {
        title: 'Chr. D. IX Gade, København',
        category: 'Granitfacade & Butiksarkade',
        desc: '100% tør afrensning af sokkelmaling og graffitirester ved befærdet indgangsparti.',
        image: '/cdn-cgi/image/width=600,quality=80,format=auto/_assets/granitfacade-chr-d-9-c2-b4s-gade-650x650.jpg',
        link: '/cases/granitfacade-rensning-kbh-trafikfilm-sokkelmaling'
      }
    ]
  },

  aarhus: {
    cityName: 'Aarhus',
    regionName: 'Aarhus og Østjylland',
    slug: 'laserrensning-aarhus',
    h1: 'Laserrensning i Aarhus til byggeprojekter og tekniske overflader',
    metaTitle: 'Laserrensning i Aarhus til facader og byggeprojekter | Hør mere',
    metaDesc:
      'Professionel og mobil laserrensning i Aarhus og Østjylland. Skånsom laserafrensning af facader, murværk, beton og procesudstyr. Kontakt os for tilbud.',
    ogImage: 'https://ecolaser.dk/cdn-cgi/image/width=1800,quality=80,format=auto/_assets/img-3498.jpg',
    heroImage: '/cdn-cgi/image/width=1800,quality=80,format=auto/_assets/img-3498.jpg',
    sidebarImage: '/cdn-cgi/image/width=800,quality=85,format=auto/_assets/imgi-70-img-20260502-095031-1300x1300.jpg',
    topBanner: {
      image: '/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/img-3498.jpg',
      alt: 'Håndværker udfører kontrolleret laserrensning på en murstensfacade i Aarhus',
      caption: 'Kontrolleret og skånsom facaderens med mobil laser – 100% tør proces uden vand eller kemi'
    },
    intro: [
      <>
        Ecolaser udfører professionel{' '}
        <Link
          to="/"
          className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
        >
          laserrensning i Aarhus
        </Link>{' '}
        for bygherrer, entreprenører, rådgivere, ejendomsselskaber, virksomheder og offentlige aktører. Med mobilt laserudstyr kan vi udføre kontrolleret afrensning direkte på byggepladsen, ejendommen eller produktionsstedet.
      </>,
      'Vores løsninger inden for laserrens anvendes blandt andet på murværk, beton, natursten, metal og tekniske overflader. Processen foregår uden procesvand, rensekemi og blæsemidler og kan indgå i både nybyggeri, renovering, restaurering og industrielle vedligeholdelsesopgaver.'
    ],
    sections: [
      {
        id: 'nybyggeri-renovering',
        title: 'Laserafrensning til nybyggeri og renovering i Aarhus',
        paragraphs: [
          'Ved nybyggeri og større renoveringsprojekter kan synlige belægninger på murværk og andre overflader påvirke det færdige resultat. Kalkslør, salpeterudtræk, byggesnavs, sod og andre fastsiddende belægninger kræver forskellige indstillinger og arbejdsmetoder.',
          'Ved laserafrensning i Aarhus vurderer vi både materialets hårdhed, porøsitet og erosionsgrad samt belægningens karakter og tykkelse. Laserens intensitet og frekvens kalibreres efter den konkrete overflade.',
          'For bygherre, entreprenør og rådgiver betyder det, at afrensningen kan planlægges i forhold til projektets øvrige arbejder og de krav, der stilles til den færdige overflade.'
        ]
      },
      {
        id: 'opgaver-i-byen',
        title: 'Laserrens kan blandt andet bruges til',
        introText: 'Vores mobile laserteknologi dækker en lang række opgaver på byggepladser, ejendomme og industrivirksomheder i Aarhus:',
        bullets: [
          'Kalkslør og salpeterudtræk på nyt og eksisterende murværk',
          'Sod, trafikfilm og atmosfærisk smuds på facader',
          <>
            <Link
              to="/ydelser/graffiti-beton-natursten"
              className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
            >
              Graffiti på egnede murstens-, beton- og naturstensoverflader
            </Link>
          </>,
          <>
            <Link
              to="/ydelser/rust-metaloverflader"
              className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
            >
              Rust og fastsiddende belægninger på metal
            </Link>
          </>,
          'Procesudstyr, støbeforme og tekniske komponenter',
          'Historiske bygningsdele, stuk, friser og ornamenter'
        ]
      },
      {
        id: 'facaderens-murvaerk',
        title: 'Laserrens af mursten og facader i Aarhus',
        paragraphs: [
          'Murværk varierer betydeligt i både alder, brænding, fuger og overfladens tilstand. Derfor bør laserrens af mursten ikke udføres efter én fast standard.',
          'På facadeopgaver vurderer vi blandt andet murstenens brandhud, erosionsgrad og fugernes tilstand, før behandlingen fastlægges. Da laseren arbejder uden mekanisk slibning og ikke tilfører procesvand til murstenenes kapillarer, kan metoden være relevant, hvor overfladen kræver en kontrolleret behandling.',
          <>
            På projekter med særlige krav til murværket anvender vi samme principper som ved vores{' '}
            <Link
              to="/ydelser/facaderens-murvaerk"
              className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
            >
              facaderens med laser til murværk
            </Link>
            , hvor materiale og belægning vurderes individuelt.
          </>
        ],
        showcase: {
          image: '/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/graffitifjernelse-roed-mursten-aarhus-650x650.jpg',
          alt: 'Graffitifjernelse på rød mursten ved Klostertorvet i Aarhus',
          caption: 'Klostertorvet, Aarhus: Skånsom graffitifjernelse på historiske røde mursten uden mekanisk slitage.'
        }
      },
      {
        id: 'historiske-ejendomme',
        title: 'Skånsom afrensning af historiske ejendomme',
        paragraphs: [
          'Aarhus rummer mange ældre, historiske og bevaringsværdige bygninger, hvor overflader ikke nødvendigvis tåler traditionelle rensemetoder. Natursten, historiske tegl, gipsarbejder og detaljerede ornamenter kan kræve en mere præcis tilgang.',
          'Laserens parametre kan tilpasses blandt andet sandsten, kalksten, marmor, granit, stuk og friser efter en konkret materialevurdering. Ved særligt følsomme overflader kan en prøverensning bruges til at fastlægge renseniveau og arbejdsmetode.',
          <>
            Denne tilgang anvender vi også ved{' '}
            <Link
              to="/ydelser/fredede-bygninger"
              className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
            >
              laserrensning af fredede og bevaringsværdige bygninger
            </Link>
            , hvor historisk patina, profileringer og oprindelig overfladestruktur indgår i vurderingen.
          </>
        ],
        showcase: {
          image: '/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/graffitifjernelse-granitmur-kontrast-650x650.jpg',
          alt: 'Laserrensning af granitmur i Østjylland',
          caption: 'Præcisionslaserafrensning af natursten og granit uden kemi eller overfladeskader.'
        }
      },
      {
        id: 'industriel-laserrens',
        title: 'Industriel laserrensning direkte hos virksomheden',
        paragraphs: [
          'Laserrens er ikke kun relevant på bygninger. Ecolaser udfører også industriel laserrensning i Aarhus på blandt andet procesudstyr, støbeforme, værktøj og metaloverflader.',
          'Rust, olie, fedt, maling og procesbelægninger kan behandles målrettet, når materialet egner sig til metoden. Fordi udstyret er mobilt, kan opgaven udføres direkte på produktionsstedet og planlægges med hensyn til den øvrige drift.',
          <>
            Ved denne type opgaver arbejder vi efter samme principper som inden for vores{' '}
            <Link
              to="/ydelser/industri-skadeservice"
              className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
            >
              laserrens til industri og skadeservice
            </Link>
            . Når arbejdet kræver det, kan punktudsugning med HEPA-filtrering anvendes til støv og forstøvede restpartikler.
          </>
        ]
      },
      {
        id: 'faa-vurderet-opgave',
        title: 'Få vurderet en opgave med laserrens i Aarhus',
        paragraphs: [
          'Ecolaser har base i Ryomgård på Djursland og udfører mobile opgaver med laserrens og laserafrensning i Aarhus og resten af Østjylland.',
          'Ved større eller komplekse projekter vurderer vi blandt andet materiale, belægning, areal, adgangsforhold og opgavens samlede omfang. En besigtigelse eller prøverensning kan være relevant, før den endelige metode og pris fastlægges.',
          'Ring på 29 10 08 10, skriv til info@ecolaser.dk eller kontakt os for en faglig vurdering og et uforpligtende tilbud.'
        ]
      }
    ],
    cases: [
      {
        title: 'Mørke Mursten & Kalkudtræk',
        category: 'Murværk & Facade',
        desc: 'Målrettet fjernelse af salpeterudtræk og hvidt kalkslør uden kemikalier eller misfarvning af fugerne.',
        image: '/cdn-cgi/image/width=600,quality=80,format=auto/_assets/imgi-13-0820-1.png',
        link: '/cases/fjernelse-salpeterudtraek-mursten-kemifri-laser'
      },
      {
        title: 'Klostertorvet, Aarhus',
        category: 'Røde Mursten & Graffiti',
        desc: 'Komplet graffitifjernelse fra ældre murværk i Aarhus C med bevarelse af teglens oprindelige brandhud.',
        image: '/cdn-cgi/image/width=600,quality=80,format=auto/_assets/graffitifjernelse-roed-mursten-aarhus-650x650.jpg',
        link: '/cases'
      },
      {
        title: 'Sorø Stationsbygning',
        category: 'Historisk Murværk',
        desc: 'Afrensning af 170 år gammelt murværk med kontrolleret laserrens og bevarelse af den vitale brandhud.',
        image: '/cdn-cgi/image/width=600,quality=80,format=auto/_assets/soro-station-650x650.jpg',
        link: '/cases/komplet-afrensning-af-historisk-stationsbygning-i-soroe-fra-1856'
      }
    ]
  },

  odense: {
    cityName: 'Odense',
    regionName: 'Odense og Fyn',
    slug: 'laserrensning-odense',
    h1: 'Laserrensning i Odense til byggeri, facade og industri',
    metaTitle: 'Laserrensning i Odense til facade, byggeri og industri | Få tilbud',
    metaDesc:
      'Mobil laserrensning i Odense og på Fyn. Skånsom afrensning af murværk, facader, historiske ejendomme og tekniske installationer. Få et tilbud i dag.',
    heroImage: '/cdn-cgi/image/width=1800,quality=80,format=auto/_assets/img-20260514-075654.jpg',
    sidebarImage: '/cdn-cgi/image/width=800,quality=85,format=auto/_assets/soro-station-650x650.jpg',
    topBanner: {
      image: '/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/img-20260514-075654.jpg',
      alt: 'Facadearbejde med stillads og EcoLaser-firmabil foran murstensbygning',
      caption: 'Mobil opstilling i Odense og på Fyn: Kontrolleret facaderens direkte fra lift eller stillads'
    },
    intro: [
      <>
        Ecolaser udfører professionel og mobil{' '}
        <Link
          to="/"
          className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
        >
          laserrensning i Odense
        </Link>{' '}
        for bygherrer, entreprenører, rådgivere, virksomheder, ejendomsselskaber og offentlige aktører. Vi arbejder med kontrolleret laserafrensning af blandt andet facader, murværk, natursten, metal og tekniske overflader som en del af vores samlede løsninger inden for laserrens.
      </>,
      'Metoden arbejder uden procesvand, rensekemi og blæsemidler og kan derfor være relevant ved nybyggeri, renovering, restaurering, vedligeholdelse og industrielle projekter. Fra vores base i Ryomgård kører vi med mobilt udstyr til Odense og resten af Fyn.'
    ],
    sections: [
      {
        id: 'nybyggeri-renovering',
        title: 'Laserafrensning i Odense til nybyggeri og renovering',
        paragraphs: [
          'Ved større bygge- og renoveringsprojekter kan der opstå behov for præcis afrensning af murværk, beton, natursten eller andre overflader. Det kan eksempelvis være kalkslør, salpeterudtræk, sod, graffiti, rust eller andre fastsiddende belægninger.',
          'Ved laserafrensning i Odense vurderer vi materialets hårdhed, porøsitet, tilstand og overflade samt den belægning, der skal fjernes. Intensitet og frekvens tilpasses derefter den konkrete opgave.',
          'Det gør metoden relevant for både specialopgaver og større projekter, hvor afrensningen skal kunne planlægges som en del af den samlede byggeproces.'
        ]
      },
      {
        id: 'opgaver-i-byen',
        title: 'Typiske opgaver med laserrens i Odense',
        introText: 'Vores mobile anlæg betjener byggepladser, ejendomsselskaber og produktionsvirksomheder på Fyn:',
        bullets: [
          'Facader og murværk med kalkslør, salpeterudtræk og atmosfærisk smuds',
          <>
            <Link
              to="/ydelser/graffiti-beton-natursten"
              className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
            >
              Graffiti og sod på egnede murstens-, beton- og naturstensoverflader
            </Link>
          </>,
          'Historiske facader, stuk, friser og ornamenter',
          <>
            <Link
              to="/ydelser/rust-metaloverflader"
              className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
            >
              Rust og belægninger på metal og tekniske overflader
            </Link>
          </>,
          'Procesudstyr, støbeforme og industrielle komponenter',
          'Afrensning i forbindelse med nybyggeri og renovering'
        ]
      },
      {
        id: 'facaderens-murvaerk',
        title: 'Facaderens med laser i Odense',
        paragraphs: [
          'På facader og murværk er ingen overflader ens. Nye mursten, ældre tegl, fuger og natursten kan reagere forskelligt på afrensning, og derfor tager vi udgangspunkt i det konkrete materiale.',
          <>
            Laserrens af mursten kan blandt andet anvendes til kalkslør, salpeterudtræk, atmosfærisk smuds og udvalgte belægninger. På projekter, hvor murværk og facade kræver en mere specialiseret behandling, arbejder vi med{' '}
            <Link
              to="/ydelser/facaderens-murvaerk"
              className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
            >
              facaderens med laser til murværk
            </Link>
            , hvor metoden tilpasses overfladens tilstand og sårbarhed.
          </>,
          'Da processen ikke tilfører procesvand, begrænses fugtpåvirkningen af murværket under selve behandlingen. Ved følsomme eller større projekter kan en prøverensning være relevant, før den endelige metode fastlægges.'
        ],
        showcase: {
          image: '/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/gilleleje-3-1024x461.jpg',
          alt: 'Afrensning af murværk for kalkslør uden syre i Odense',
          caption: 'Fjernelse af kalk- og mørtelslør på murværk uden syre eller skader på fugerne.'
        }
      },
      {
        id: 'historiske-ejendomme',
        title: 'Laserrensning af historiske og bevaringsværdige bygninger',
        paragraphs: [
          'Odense har både ældre ejendomme og bevaringsværdige bygningsmiljøer, hvor afrensning kræver særlig materialeforståelse. Laserteknologien kan efter en konkret vurdering anvendes på blandt andet sandsten, kalksten, marmor, granit, gips, friser, stuk og ornamenter.',
          <>
            Behandlingen tilpasses materialets varmefølsomhed, erosionsgrad, geometri og ønskede renseniveau. Det er samme tilgang, vi anvender ved{' '}
            <Link
              to="/ydelser/fredede-bygninger"
              className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
            >
              laserrensning af fredede og bevaringsværdige bygninger
            </Link>
            , hvor historisk patina og overfladestruktur skal bevares med størst mulig respekt for det oprindelige materiale.
          </>
        ],
        showcase: {
          image: '/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/graffiti-frredet-hus-scaled.jpg',
          alt: 'Historisk bindingsværk og bevaringsværdig facade på Fyn',
          caption: 'Fredet bindingsværk på Fyn (Bondos Gård): Skånsom graffitifjernelse uden kemi på historisk puds og træværk.'
        }
      },
      {
        id: 'industri-teknik',
        title: 'Laserrens til industri og tekniske overflader i Odense',
        paragraphs: [
          'Ecolaser udfører også laserrens på procesudstyr, støbeforme, metal og andre tekniske overflader i Odense. Metoden kan blandt andet anvendes til rust, olie, fedt, maling og fastsiddende procesbelægninger, når materialet egner sig til behandlingen.',
          <>
            Det mobile udstyr gør det muligt at udføre arbejdet direkte på produktionsstedet, byggepladsen eller ejendommen. Ved opgaver på procesudstyr og tekniske overflader indgår arbejdet som en del af vores{' '}
            <Link
              to="/ydelser/industri-skadeservice"
              className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
            >
              laserrens til industri og skadeservice
            </Link>
            .
          </>,
          'Når opgaven kræver det, kan der anvendes punktudsugning tæt ved arbejdsområdet, hvor støv og forstøvede restpartikler filtreres gennem HEPA-filtre. Udstyret kan desuden anvendes fra lift og stillads, når adgangsforholdene kræver det.'
        ]
      },
      {
        id: 'mobil-daekning',
        title: 'Mobil laserrensning i Odense og på Fyn',
        paragraphs: [
          'Ecolaser hjælper med projekter i Odense og på resten af Fyn, hvor der er behov for præcis og kontrolleret afrensning. Vi kan indgå i projekter for blandt andet bygherrer, entreprenører, rådgivere, kommunale og andre offentlige aktører samt virksomheder og ejendomsansvarlige.',
          'Ved større eller mere komplekse opgaver kan en besigtigelse eller prøverensning være relevant. Materiale, belægning, adgangsforhold, areal og opgavens samlede omfang indgår i vurderingen, før metode og tilbud fastlægges.',
          'Ring på 29 10 08 10, skriv til info@ecolaser.dk eller kontakt os for en faglig vurdering af projektet og et uforpligtende tilbud.'
        ]
      }
    ],
    cases: [
      {
        title: 'Bondos Gård, Rudkøbing',
        category: 'Fredet Bindingsværk',
        desc: 'Skånsom fjernelse af graffitimaling på kalket puds og historisk egetræsbjælker uden kemi.',
        image: '/cdn-cgi/image/width=600,quality=80,format=auto/_assets/bondos-gard-650x650.jpg',
        link: '/cases/laserrensning-fredet-bindingsvaerk-rudkobing'
      },
      {
        title: 'Mørke Mursten & Kalkudtræk',
        category: 'Murværk & Facader',
        desc: 'Målrettet fjernelse af salpeterudtræk og hvide kalkslør uden misfarvning af de originale fuger.',
        image: '/cdn-cgi/image/width=600,quality=80,format=auto/_assets/imgi-13-0820-1.png',
        link: '/cases/fjernelse-salpeterudtraek-mursten-kemifri-laser'
      },
      {
        title: 'Sorø Stationsbygning',
        category: 'Historisk Murværk',
        desc: 'Udført på station i fuld drift med natarbejde og bevarelse af den vitale brandhud på murværket.',
        image: '/cdn-cgi/image/width=600,quality=80,format=auto/_assets/soro-station-650x650.jpg',
        link: '/cases/komplet-afrensning-af-historisk-stationsbygning-i-soroe-fra-1856'
      }
    ]
  }
};

export default function CityLanding({ cityKey }) {
  const location = useLocation();

  // Resolve active city
  let resolvedKey = cityKey;
  if (!resolvedKey) {
    const path = location.pathname.toLowerCase();
    if (path.includes('aarhus')) resolvedKey = 'aarhus';
    else if (path.includes('odense')) resolvedKey = 'odense';
    else if (path.includes('koebenhavn') || path.includes('kobenhavn')) resolvedKey = 'koebenhavn';
  }
  if (!resolvedKey) resolvedKey = 'koebenhavn';

  const data = CITIES_DATA[resolvedKey] || CITIES_DATA.koebenhavn;
  const phone = contactInfo.phone || '29 10 08 10';
  const email = contactInfo.email || 'info@ecolaser.dk';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, resolvedKey]);

  return (
    <>
      <Helmet>
        <title>{data.metaTitle}</title>
        <meta name="description" content={data.metaDesc} />
        <meta property="og:title" content={data.metaTitle} />
        <meta property="og:description" content={data.metaDesc} />
        <meta property="og:image" content={data.ogImage || (data.heroImage?.startsWith('http') ? data.heroImage : `https://ecolaser.dk${data.heroImage}`)} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={data.metaTitle} />
        <meta name="twitter:description" content={data.metaDesc} />
        <meta name="twitter:image" content={data.ogImage || (data.heroImage?.startsWith('http') ? data.heroImage : `https://ecolaser.dk${data.heroImage}`)} />
        <link rel="canonical" href={`https://ecolaser.dk/${data.slug}`} />
        <html lang="da" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: data.h1,
            description: data.metaDesc,
            provider: {
              '@type': 'LocalBusiness',
              name: 'Ecolaser ApS',
              telephone: '+4529100810',
              email: 'info@ecolaser.dk',
              address: {
                '@type': 'PostalAddress',
                streetAddress: 'Kirkevej 16',
                addressLocality: 'Ryomgård',
                postalCode: '8550',
                addressCountry: 'DK'
              },
              areaServed: [
                {
                  '@type': 'City',
                  name: data.cityName
                },
                {
                  '@type': 'Country',
                  name: 'Danmark'
                }
              ]
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section – matcher ServiceDetail.jsx 1:1 */}
      <section className="relative pt-44 pb-28 md:pt-52 md:pb-36 overflow-hidden bg-zinc-950">
        <div className="absolute inset-0 z-0">
          <img
            src={data.heroImage}
            alt={data.h1}
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-zinc-950/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-left flex flex-col items-start gap-3">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-tight">
            {data.h1}
          </h1>
        </div>
      </section>

      {/* Main Content Section – matcher ServiceDetail.jsx 1:1 */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            
            {/* Left Column: Indhold, billeder, fordele og cases */}
            <div className="lg:col-span-7 space-y-12">
              <div className="space-y-10 text-zinc-700 leading-relaxed text-base md:text-lg">
                
                {/* Intro tekster med interne links */}
                <div className="space-y-4">
                  {data.intro.map((p, idx) => (
                    <p key={idx} className="text-zinc-700 leading-relaxed text-base md:text-lg">
                      {p}
                    </p>
                  ))}
                </div>

                {/* Top Image Showcase Banner */}
                {data.topBanner && (
                  <div className="rounded-2xl overflow-hidden border border-zinc-100 bg-zinc-50 shadow-sm">
                    <div className="relative aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
                      <img
                        src={data.topBanner.image}
                        alt={data.topBanner.alt}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/75 via-zinc-950/20 to-transparent flex items-end p-4 md:p-5">
                        <div className="text-white text-xs md:text-sm flex items-center gap-2 font-medium">
                          <span className="w-2 h-2 rounded-full bg-brand-green shrink-0 animate-pulse" />
                          <span>{data.topBanner.caption}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Primære CTA-knapper */}
                <div className="pt-2 flex flex-wrap items-center gap-4">
                  <a
                    href={`tel:${phone}`}
                    className="inline-flex items-center gap-2.5 bg-brand-green-dark hover:bg-brand-green text-white font-bold px-6 py-3.5 rounded-xl text-sm transition-all duration-300 shadow-sm"
                  >
                    <i className="fa-solid fa-phone text-white" aria-hidden="true" />
                    <span className="text-white">Ring 29 10 08 10</span>
                  </a>
                  <a
                    href={`mailto:${email}`}
                    className="inline-flex items-center gap-2.5 bg-zinc-900 hover:bg-zinc-800 text-white font-medium px-6 py-3.5 rounded-xl text-sm transition-all duration-300 border border-zinc-800"
                  >
                    <i className="fa-solid fa-envelope" aria-hidden="true" />
                    <span>Send en mail</span>
                  </a>
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2.5 bg-white hover:bg-zinc-100 text-zinc-900 font-semibold px-6 py-3.5 rounded-xl text-sm transition-all duration-300 border border-zinc-200"
                  >
                    <i className="fa-solid fa-calculator text-brand-green" aria-hidden="true" />
                    <span>Få et tilbud</span>
                  </Link>
                </div>

                {/* Sektioner */}
                {data.sections.map((section, sIdx) => (
                  <div key={section.id || sIdx} className="space-y-4 pt-6 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      {section.title}
                    </h2>

                    {section.introText && (
                      <p className="text-zinc-700 leading-relaxed text-base md:text-lg">
                        {section.introText}
                      </p>
                    )}

                    {section.paragraphs && section.paragraphs.map((p, pIdx) => (
                      <p key={pIdx} className="text-zinc-700 leading-relaxed text-base md:text-lg">
                        {p}
                      </p>
                    ))}

                    {section.bullets && (
                      <ul className="space-y-2.5 pt-2">
                        {section.bullets.map((b, bIdx) => (
                          <li key={bIdx} className="flex items-start gap-3 text-zinc-700 text-base md:text-lg">
                            <span className="w-5 h-5 rounded-full bg-brand-green/15 text-brand-green flex items-center justify-center shrink-0 mt-1">
                              <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                            </span>
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {section.serviceLink && (
                      <div className="pt-2">
                        <Link
                          to={section.serviceLink.to}
                          className="inline-flex items-center gap-2 font-bold text-sm text-brand-green hover:underline uppercase tracking-wider"
                        >
                          <span>{section.serviceLink.text}</span>
                          <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                        </Link>
                      </div>
                    )}

                    {/* Billedshowcase under relevant sektion */}
                    {section.showcase && (
                      <div className="pt-3">
                        <div className="rounded-2xl overflow-hidden border border-zinc-100 bg-zinc-50 shadow-sm">
                          <div className="relative aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
                            <img
                              src={section.showcase.image}
                              alt={section.showcase.alt}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/75 via-zinc-950/20 to-transparent flex items-end p-4 md:p-5">
                              <div className="text-white text-xs md:text-sm font-medium">
                                {section.showcase.caption}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Mid-content CTA Box */}
                <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden shadow-xl border border-zinc-800/80 my-8">
                  <div className="relative z-10 space-y-4">
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-green/20 text-brand-green text-xs font-semibold uppercase tracking-wider">
                      <i className="fa-solid fa-wand-magic-sparkles text-xs" /> Prøverensning direkte på stedet
                    </span>
                    <h3 className="text-xl sm:text-2xl font-black text-white">
                      Få en professionel vurdering af din opgave i {data.cityName}
                    </h3>
                    <p className="text-zinc-300 text-sm sm:text-base leading-relaxed max-w-2xl">
                      Vi tilbyder prøverensning direkte på ejendommen eller byggepladsen, så bygherre, entreprenør og rådgiver kan godkende resultatet, før den fulde opgave igangsættes.
                    </p>
                    <div className="pt-2 flex flex-wrap items-center gap-3">
                      <a
                        href={`tel:${phone}`}
                        className="inline-flex items-center gap-2 bg-brand-green-dark hover:bg-brand-green text-white font-bold px-5 py-3 rounded-xl text-xs sm:text-sm transition-all duration-300 shadow-sm"
                      >
                        <i className="fa-solid fa-phone" /> Ring 29 10 08 10
                      </a>
                      <a
                        href={`mailto:${email}`}
                        className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium px-5 py-3 rounded-xl text-xs sm:text-sm backdrop-blur-sm border border-white/20 transition-all duration-300"
                      >
                        <i className="fa-solid fa-envelope" /> Send en mail
                      </a>
                      <Link
                        to="/kontakt"
                        className="inline-flex items-center gap-2 bg-transparent hover:bg-white/10 text-white font-medium px-5 py-3 rounded-xl text-xs sm:text-sm border border-white/20 transition-all duration-300"
                      >
                        <i className="fa-solid fa-arrow-right text-brand-green" /> Indhent tilbud
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Fordele og Arbejdsprocessen container – matcher ServiceDetail.jsx 1:1 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8 border-t border-zinc-100">
                  {/* Fordele Card */}
                  <div className="bg-zinc-50 rounded-2xl p-6 md:p-8">
                    <h3 className="text-lg font-bold text-zinc-900 mb-6">
                      Fordele ved laserrens
                    </h3>
                    <ul className="space-y-4">
                      <li className="flex items-start gap-3 text-sm text-zinc-600 leading-relaxed">
                        <div className="w-1.5 h-1.5 rounded-full bg-zinc-900 mt-2 shrink-0" />
                        <span>Helt unik til indendørs afrensning, da processen er 100% vandfri</span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-zinc-600 leading-relaxed">
                        <div className="w-1.5 h-1.5 rounded-full bg-zinc-900 mt-2 shrink-0" />
                        <span>Skånsom mod murstenens brandhud og historisk kulturarv</span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-zinc-600 leading-relaxed">
                        <div className="w-1.5 h-1.5 rounded-full bg-zinc-900 mt-2 shrink-0" />
                        <span>Uden kemikalier, syre og blæsemidler – ingen affaldsstrømme</span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-zinc-600 leading-relaxed">
                        <div className="w-1.5 h-1.5 rounded-full bg-zinc-900 mt-2 shrink-0" />
                        <span>Punktudsugning med HEPA-filtrering tæt ved arbejdsområdet</span>
                      </li>
                    </ul>
                  </div>

                  {/* Arbejdsprocessen Card */}
                  <div className="bg-zinc-50 rounded-2xl p-6 md:p-8">
                    <h3 className="text-lg font-bold text-zinc-900 mb-6">
                      Arbejdsprocessen
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3 text-sm text-zinc-600 leading-relaxed">
                        <div className="w-1.5 h-1.5 rounded-full bg-zinc-900 mt-2 shrink-0" />
                        <span>Faglig besigtigelse: Vi vurderer materiale, belægning og adgangsforhold.</span>
                      </div>
                      <div className="flex items-start gap-3 text-sm text-zinc-600 leading-relaxed">
                        <div className="w-1.5 h-1.5 rounded-full bg-zinc-900 mt-2 shrink-0" />
                        <span>Prøverensning: Testfelt etableres på overfladen for at fastlægge renseniveau.</span>
                      </div>
                      <div className="flex items-start gap-3 text-sm text-zinc-600 leading-relaxed">
                        <div className="w-1.5 h-1.5 rounded-full bg-zinc-900 mt-2 shrink-0" />
                        <span>Mobil udførelse: Vi arbejder fra lift, stillads eller indendørs uden spild.</span>
                      </div>
                      <div className="flex items-start gap-3 text-sm text-zinc-600 leading-relaxed">
                        <div className="w-1.5 h-1.5 rounded-full bg-zinc-900 mt-2 shrink-0" />
                        <span>Kvalitetssikring: Færdigt resultat afleveres til bygherre eller rådgiver.</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Relevante referencecases for byen */}
                {data.cases && data.cases.length > 0 && (
                  <div className="space-y-6 pt-10 border-t border-zinc-100">
                    <div className="flex items-end justify-between">
                      <div>
                        <span className="text-xs font-bold text-brand-green uppercase tracking-wider">
                          Dokumenterede resultater
                        </span>
                        <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                          Udvalgte referencecases
                        </h2>
                      </div>
                      <Link
                        to="/cases"
                        className="hidden sm:inline-flex items-center gap-2 text-xs font-bold text-zinc-600 hover:text-zinc-950 uppercase tracking-wider"
                      >
                        <span>Se alle cases</span>
                        <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                      </Link>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 pt-2">
                      {data.cases.map((c, cIdx) => (
                        <Link
                          key={cIdx}
                          to={c.link}
                          className="group bg-zinc-50 rounded-2xl overflow-hidden border border-zinc-100 hover:border-brand-green/40 hover:shadow-md transition-all duration-300 flex flex-col"
                        >
                          <div className="aspect-[4/3] overflow-hidden bg-zinc-200">
                            <img
                              src={c.image}
                              alt={c.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                          <div className="p-4 flex-1 flex flex-col justify-between">
                            <div>
                              <span className="text-[11px] font-semibold text-brand-green uppercase tracking-wider block mb-1">
                                {c.category}
                              </span>
                              <h4 className="text-sm font-bold text-zinc-900 group-hover:text-brand-green transition-colors leading-snug">
                                {c.title}
                              </h4>
                              <p className="text-xs text-zinc-500 mt-1 line-clamp-2">
                                {c.desc}
                              </p>
                            </div>
                            <span className="text-xs font-semibold text-zinc-900 flex items-center gap-1.5 mt-3 group-hover:translate-x-1 transition-transform">
                              Læs case <i className="fa-solid fa-arrow-right text-[10px] text-brand-green" />
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Billede og hurtig kontaktboks – matcher ServiceDetail.jsx 1:1 */}
            <div className="lg:col-span-5 lg:sticky lg:top-28 space-y-6">
              
              {/* Billede i højre kolonne */}
              <div className="rounded-2xl overflow-hidden shadow-sm bg-zinc-100 aspect-[4/3]">
                <img
                  src={data.sidebarImage}
                  alt={`Mobil laserrensning i ${data.cityName}`}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Specialist- og dækningsboks */}
              <div className="bg-zinc-50 border border-zinc-100 rounded-2xl p-6 md:p-8 space-y-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-brand-green/15 text-brand-green flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-location-dot text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-zinc-900">Mobil laserrensning i {data.cityName}</h4>
                    <p className="text-xs text-zinc-500">{data.regionName}</p>
                  </div>
                </div>

                <p className="text-sm text-zinc-600 leading-relaxed">
                  Ecolaser kører mobilt ud til både akutte og planlagte opgaver for bygherrer, rådgivere, entreprenører og ejendomsselskaber i {data.cityName}.
                </p>

                <ul className="space-y-2 text-xs text-zinc-600 border-t border-zinc-200/80 pt-4">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-brand-green" /> 100% tør proces uden procesvand
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-brand-green" /> Fri for kemikalier og blæsemidler
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-brand-green" /> Mobilt udstyr til lift og stillads
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-brand-green" /> Punktudsugning med HEPA-filtrering
                  </li>
                </ul>

                <div className="pt-2 border-t border-zinc-200/80 space-y-3">
                  <a
                    href={`tel:${phone}`}
                    className="w-full bg-brand-green-dark hover:bg-brand-green text-white font-bold py-3 rounded-xl text-xs uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2 shadow-sm"
                  >
                    <i className="fa-solid fa-phone" /> Ring direkte: 29 10 08 10
                  </a>
                  <a
                    href={`mailto:${email}`}
                    className="w-full bg-white hover:bg-zinc-100 text-zinc-900 font-semibold py-3 rounded-xl text-xs uppercase tracking-wider border border-zinc-200 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <i className="fa-solid fa-envelope" /> info@ecolaser.dk
                  </a>
                </div>
              </div>

              {/* Rådgivnings- og kontaktgenvej */}
              <div className="bg-zinc-900 rounded-2xl p-6 md:p-8 text-white space-y-4">
                <span className="text-xs font-semibold text-brand-green uppercase tracking-wider">
                  Faglig rådgivning
                </span>
                <h4 className="text-lg font-bold">Har du en opgave i {data.cityName}?</h4>
                <p className="text-xs text-zinc-300 leading-relaxed">
                  Send os billeder eller beskrivelse af din overflade, så vender vi hurtigt tilbage med en faglig vurdering eller tilbud på prøverensning.
                </p>
                <Link
                  to="/kontakt"
                  className="w-full bg-brand-green hover:bg-brand-green-light text-zinc-950 font-bold py-3 rounded-xl text-xs uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2 block text-center"
                >
                  <span>Gå til kontaktformular</span>
                  <i className="fa-solid fa-arrow-right text-xs" />
                </Link>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Stor bundsektion med direkte kontakt – matcher resten af sitet */}
      <section className="bg-zinc-950 py-20 text-white border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 text-center space-y-8">
          <div className="max-w-3xl mx-auto space-y-4">
            <span className="text-xs font-bold text-brand-green uppercase tracking-wider">
              {data.cityName} &bull; {data.regionName}
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight">
              Klar til kontrolleret laserrensning?
            </h2>
            <p className="text-zinc-400 text-base md:text-lg">
              Kontakt Ecolaser for en uforpligtende samtale og en faglig vurdering af dit projekt i {data.cityName}.
            </p>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-4">
            <a
              href={`tel:${phone}`}
              className="inline-flex items-center gap-3 bg-brand-green hover:bg-brand-green-light text-zinc-950 font-bold px-8 py-4 rounded-xl text-base transition-all duration-300 shadow-lg shadow-brand-green/20"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <span>Ring {phone}</span>
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-3 bg-white/10 hover:bg-white/15 text-white font-semibold px-8 py-4 rounded-xl text-base border border-white/15 transition-all duration-300"
            >
              <i className="fa-solid fa-calculator text-brand-green" aria-hidden="true" />
              <span>Indhent uforpligtende tilbud</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
